import { useState, useEffect } from "react";
import jwtDecode from "jwt-decode";
import storageNames from "web/constants/storageNames";
import BrowserPersistence from "web/utils/system/storage/storage/browserPersistence";

const storage = new BrowserPersistence();

const useOneKcId = (): string | null => {
    const [oneKcId, setOneKcId] = useState<string | null>(() => {
        try {
            const accessToken = storage.getItem(storageNames.tokenAccess);
            if (!accessToken) return null;
            const decoded = jwtDecode<{ sub: string }>(accessToken);
            return decoded.sub ?? null;
        } catch {
            return null;
        }
    });

    useEffect(() => {
        const updateOneKcId = () => {
            try {
                const accessToken = storage.getItem(storageNames.tokenAccess);
                if (!accessToken) {
                    setOneKcId(null);
                    return;
                }
                const decoded = jwtDecode<{ sub: string }>(accessToken);

                if (decoded.sub === oneKcId) {
                    return;
                }

                setOneKcId(decoded.sub ?? null);
            } catch {
                setOneKcId(null);
            }
        };

        // storage events (cross-tab changes)
        const handleStorageChange = (e: StorageEvent) => {
            const key = `${BrowserPersistence.KEY}__${storageNames.tokenAccess}`;
            if (e.key === key || e.key === null) {
                updateOneKcId();
            }
        };

        // catches token refreshes that happened while tab was hidden
        const handleVisibilityChange = () => {
            if (document.visibilityState === "visible") {
                updateOneKcId();
            }
        };

        window.addEventListener("storage", handleStorageChange);
        document.addEventListener("visibilitychange", handleVisibilityChange);

        return () => {
            window.removeEventListener("storage", handleStorageChange);
            document.removeEventListener("visibilitychange", handleVisibilityChange);
        };
    }, []);

    return oneKcId;
};

export default useOneKcId;
